/**
 * Dependency-free QR encoder for HoldThat's short pairing URLs.
 *
 * Pairing always uses QR version 10 with error-correction level L and mask 0.
 * That keeps the implementation deliberately small while leaving room for up
 * to 271 UTF-8 bytes, considerably more than the current pairing URL needs.
 */
const VERSION = 10;
const SIZE = VERSION * 4 + 17;
const DATA_CODEWORDS = 274;
const ECC_CODEWORDS_PER_BLOCK = 18;
const BLOCK_DATA_LENGTHS = [68, 68, 69, 69] as const;

function multiply(left: number, right: number): number {
  let product = 0;
  for (let index = 7; index >= 0; index--) {
    product = (product << 1) ^ ((product >>> 7) * 0x11d);
    product ^= ((right >>> index) & 1) * left;
  }
  return product;
}

function divisor(degree: number): number[] {
  const result = Array<number>(degree).fill(0);
  result[degree - 1] = 1;
  let root = 1;
  for (let index = 0; index < degree; index++) {
    for (let coefficient = 0; coefficient < degree; coefficient++) {
      result[coefficient] = multiply(result[coefficient]!, root);
      if (coefficient + 1 < degree) result[coefficient] = result[coefficient]! ^ result[coefficient + 1]!;
    }
    root = multiply(root, 0x02);
  }
  return result;
}

function remainder(data: number[], generator: number[]): number[] {
  const result = Array<number>(generator.length).fill(0);
  for (const value of data) {
    const factor = value ^ result[0]!;
    result.shift();
    result.push(0);
    for (let index = 0; index < result.length; index++) {
      result[index] = result[index]! ^ multiply(generator[index]!, factor);
    }
  }
  return result;
}

function dataCodewords(value: string): number[] {
  const payload = [...new TextEncoder().encode(value)];
  if (payload.length > 271) throw new Error('Der Gerätecode ist zu lang.');
  const bits: number[] = [];
  const append = (number: number, length: number) => {
    for (let shift = length - 1; shift >= 0; shift--) bits.push((number >>> shift) & 1);
  };
  append(0b0100, 4); // Byte mode
  append(payload.length, 16); // Version 10 uses a 16-bit byte count
  payload.forEach((byte) => append(byte, 8));
  for (let count = Math.min(4, DATA_CODEWORDS * 8 - bits.length); count > 0; count--) bits.push(0);
  while (bits.length % 8 !== 0) bits.push(0);
  const result: number[] = [];
  for (let index = 0; index < bits.length; index += 8) {
    result.push(bits.slice(index, index + 8).reduce((byte, bit) => (byte << 1) | bit, 0));
  }
  for (let pad = 0; result.length < DATA_CODEWORDS; pad++) result.push(pad % 2 === 0 ? 0xec : 0x11);
  return result;
}

function interleavedCodewords(value: string): number[] {
  const data = dataCodewords(value);
  const generator = divisor(ECC_CODEWORDS_PER_BLOCK);
  const blocks: number[][] = [];
  const checks: number[][] = [];
  let offset = 0;
  for (const length of BLOCK_DATA_LENGTHS) {
    const block = data.slice(offset, offset + length);
    blocks.push(block);
    checks.push(remainder(block, generator));
    offset += length;
  }
  const result: number[] = [];
  for (let column = 0; column < Math.max(...BLOCK_DATA_LENGTHS); column++) {
    blocks.forEach((block) => { if (column < block.length) result.push(block[column]!); });
  }
  for (let column = 0; column < ECC_CODEWORDS_PER_BLOCK; column++) {
    checks.forEach((block) => result.push(block[column]!));
  }
  return result;
}

export function encodeQRCode(value: string): boolean[][] {
  const modules = Array.from({ length: SIZE }, () => Array<boolean>(SIZE).fill(false));
  const functions = Array.from({ length: SIZE }, () => Array<boolean>(SIZE).fill(false));
  const setFunction = (x: number, y: number, dark: boolean) => {
    if (x < 0 || y < 0 || x >= SIZE || y >= SIZE) return;
    modules[y]![x] = dark;
    functions[y]![x] = true;
  };
  const finder = (centerX: number, centerY: number) => {
    for (let y = -4; y <= 4; y++) for (let x = -4; x <= 4; x++) {
      const distance = Math.max(Math.abs(x), Math.abs(y));
      setFunction(centerX + x, centerY + y, distance !== 2 && distance !== 4);
    }
  };
  const alignment = (centerX: number, centerY: number) => {
    for (let y = -2; y <= 2; y++) for (let x = -2; x <= 2; x++) {
      setFunction(centerX + x, centerY + y, Math.max(Math.abs(x), Math.abs(y)) !== 1);
    }
  };
  const bit = (number: number, index: number) => ((number >>> index) & 1) !== 0;
  const drawFormat = () => {
    const data = 0b01000; // Error correction L (01), mask 0 (000)
    let remainder = data;
    for (let index = 0; index < 10; index++) remainder = (remainder << 1) ^ (((remainder >>> 9) & 1) * 0x537);
    const format = ((data << 10) | remainder) ^ 0x5412;
    for (let index = 0; index <= 5; index++) setFunction(8, index, bit(format, index));
    setFunction(8, 7, bit(format, 6));
    setFunction(8, 8, bit(format, 7));
    setFunction(7, 8, bit(format, 8));
    for (let index = 9; index < 15; index++) setFunction(14 - index, 8, bit(format, index));
    for (let index = 0; index < 8; index++) setFunction(SIZE - 1 - index, 8, bit(format, index));
    for (let index = 8; index < 15; index++) setFunction(8, SIZE - 15 + index, bit(format, index));
    setFunction(8, SIZE - 8, true);
  };

  finder(3, 3);
  finder(SIZE - 4, 3);
  finder(3, SIZE - 4);
  for (let index = 8; index < SIZE - 8; index++) {
    setFunction(index, 6, index % 2 === 0);
    setFunction(6, index, index % 2 === 0);
  }
  [6, 28, 50].forEach((y) => [6, 28, 50].forEach((x) => {
    // Only the finder corners are excluded. Alignment patterns at (6, 28)
    // and (28, 6) must replace the timing line, not be skipped because of it.
    if (!((x === 6 && y === 6) || (x === 50 && y === 6) || (x === 6 && y === 50))) alignment(x, y);
  }));
  drawFormat();
  let versionRemainder = VERSION;
  for (let index = 0; index < 12; index++) {
    versionRemainder = (versionRemainder << 1) ^ (((versionRemainder >>> 11) & 1) * 0x1f25);
  }
  const versionBits = (VERSION << 12) | versionRemainder;
  for (let index = 0; index < 18; index++) {
    const a = SIZE - 11 + (index % 3);
    const b = Math.floor(index / 3);
    setFunction(a, b, bit(versionBits, index));
    setFunction(b, a, bit(versionBits, index));
  }

  const codewords = interleavedCodewords(value);
  let dataIndex = 0;
  for (let right = SIZE - 1; right >= 1; right -= 2) {
    if (right === 6) right--;
    const upward = ((right + 1) & 2) === 0;
    for (let vertical = 0; vertical < SIZE; vertical++) {
      const y = upward ? SIZE - 1 - vertical : vertical;
      for (let offset = 0; offset < 2; offset++) {
        const x = right - offset;
        if (functions[y]![x]) continue;
        const dark = dataIndex < codewords.length * 8
          ? ((codewords[dataIndex >>> 3]! >>> (7 - (dataIndex & 7))) & 1) !== 0
          : false;
        modules[y]![x] = dark !== ((x + y) % 2 === 0); // Mask 0
        dataIndex++;
      }
    }
  }
  drawFormat();
  return modules;
}
